import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box } from '@mui/material';
import { getChatMessageDeletionHistories } from '../api/chatMessageDeletionHistories';
import { ChatMessageDeletionHistory, ChatMessageDeletionHistorySearchParams } from '../types/chatMessageDeletionHistory';
import { DeletionHistoryTable, SearchForm } from '../components/activity/chatMessageDeletionHistory';
import { useAuth } from '../hooks/useAuth';

const STORAGE_KEY = 'chat_deletion_history_search';
const ChatMessageDeletionHistoryPage: React.FC = () => {
  const { isDeveloper } = useAuth();
  // 検索条件を取得する関数
  const getSavedFilters = () => {
    try {
      const savedState = localStorage.getItem(STORAGE_KEY);
      if (savedState) {
        return JSON.parse(savedState);
      }
    } catch (e) {
      console.error('検索条件の復元に失敗:', e);
      localStorage.removeItem(STORAGE_KEY);
    }
    return null;
  };
  // 保存された状態または初期値で状態を初期化
  const savedFilters = getSavedFilters();
  const [ deletionHistories, setDeletionHistories ] = useState<ChatMessageDeletionHistory[]>([]);
  const [ totalCount, setTotalCount ] = useState(0);
  const [ loading, setLoading ] = useState(false);
  const [ error, setError ] = useState<string | null>(null);
  // リクエスト重複防止用のref
  const requestInProgressRef = useRef<{ [key: string]: boolean }>({});
  // フォーム入力値（ユーザーの入力を保持）
  const [ messageSearchTerm, setMessageSearchTerm ] = useState(savedFilters?.messageSearchTerm || '');
  const [ selectedRoomTypes, setSelectedRoomTypes ] = useState<string[]>(savedFilters?.selectedRoomTypes || []);
  const [ messageStatus, setMessageStatus ] = useState(savedFilters?.messageStatus || 'all');
  // 実際の検索条件（検索ボタン押下時に更新）
  const [ searchParams, setSearchParams ] = useState({
    messageSearchTerm: savedFilters?.messageSearchTerm || '',
    selectedRoomTypes: savedFilters?.selectedRoomTypes || [] as string[],
    messageStatus: savedFilters?.messageStatus || 'all'
  });
  // ページネーション用の状態
  const [ page, setPage ] = useState(savedFilters?.page || 0);
  const [ rowsPerPage, setRowsPerPage ] = useState(savedFilters?.rowsPerPage || 20);
  // 削除履歴データを取得する関数
  const fetchDeletionHistories = async(params: ChatMessageDeletionHistorySearchParams) => {
    // APIパラメータのキャッシュキーを作成
    const cacheKey = JSON.stringify(params);
    // 同じパラメータでのリクエストが進行中であれば、新しいリクエストを送信しない
    if (requestInProgressRef.current[cacheKey]) {
      return;
    }
    // リクエスト開始をマーク
    requestInProgressRef.current[cacheKey] = true;
    setLoading(true);
    setError(null);
    try {
      // クエリパラメータを加工
      const apiParams = {
        ...params,
        page: params.page ? params.page + 1 : 1, // APIは1-indexedだがMUIは0-indexed
        page_size: params.page_size
      };
      const data = await getChatMessageDeletionHistories(apiParams);
      // デバッグログ: APIレスポンスを確認
      console.log('API Response:', data);
      if (data.results && data.results.length > 0) {
        console.log('First result:', data.results[0]);
        console.log('Parent user:', data.results[0].parent_user);
        console.log('Child user:', data.results[0].child_user);
        // さらに詳細なログ
        const firstItem = data.results[0];
        console.log('親ユーザー情報:', {
          parent_uid: firstItem.parent_uid,
          parent_user_type: firstItem.parent_user_type,
          parent_user: firstItem.parent_user
        });
        console.log('子ユーザー情報:', {
          child_uid: firstItem.child_uid,
          child_user_type: firstItem.child_user_type,
          child_user: firstItem.child_user
        });
      }
      // APIからの結果をセット
      if (data && data.results) {
        setDeletionHistories(data.results);
        setTotalCount(data.count);
      } else {
        // API結果がない場合は空の配列をセット
        setDeletionHistories([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error('削除履歴データの取得に失敗しました', err);
      setError('削除履歴データの取得に失敗しました。API接続を確認してください。');
      setDeletionHistories([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
      // リクエスト完了をマーク
      delete requestInProgressRef.current[cacheKey];
    }
  };
  // 検索実行関数
  const handleSearch = () => {
    // 検索条件を更新
    setSearchParams({
      messageSearchTerm,
      selectedRoomTypes,
      messageStatus
    });
    setPage(0); // ページをリセット
  };
  // 検索条件をクリア
  const handleClearFilters = () => {
    setMessageSearchTerm('');
    setSelectedRoomTypes([]);
    setMessageStatus('all');
    // 検索条件も即時クリア
    setSearchParams({
      messageSearchTerm: '',
      selectedRoomTypes: [],
      messageStatus: 'all'
    });
    setPage(0);
    localStorage.removeItem(STORAGE_KEY); // ローカルストレージからも削除
  };
  // 検索条件が変更されたときにローカルストレージに保存し、データ取得を行う
  useEffect(() => {
    // マウント状態管理
    let isMounted = true;
    // ローカルストレージに保存
    const currentState = {
      messageSearchTerm: searchParams.messageSearchTerm,
      selectedRoomTypes: searchParams.selectedRoomTypes,
      messageStatus: searchParams.messageStatus,
      page,
      rowsPerPage
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(currentState));
    // API検索パラメータの構築
    const params: ChatMessageDeletionHistorySearchParams = {
      page,
      page_size: rowsPerPage,
      search: searchParams.messageSearchTerm || undefined // Django REST Frameworkのsearch_fieldsパラメータ
    };
    // 選択されているルームタイプがある場合のみパラメータに追加
    if (searchParams.selectedRoomTypes.length > 0) {
      params.room_type = searchParams.selectedRoomTypes;
    }
    // メッセージ状態フィルター
    if (searchParams.messageStatus === 'deleted') {
      params.is_deleted = true;
    } else if (searchParams.messageStatus === 'restored') {
      params.is_restored = true;
    }
    // 遅延実行によりレンダリングと状態更新が完全に完了してから取得
    const timerId = setTimeout(() => {
      if (isMounted) {
        fetchDeletionHistories(params);
      }
    }, 10);
    // クリーンアップ関数
    return () => {
      isMounted = false;
      clearTimeout(timerId);
    };
  }, [ page, rowsPerPage, searchParams ]);
  // ページ変更ハンドラ
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };
  // 1ページあたりの表示件数変更ハンドラ
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  return (
    <Box>
      <SearchForm
        messageSearchTerm={messageSearchTerm}
        selectedRoomTypes={selectedRoomTypes}
        messageStatus={messageStatus}
        totalCount={totalCount}
        onMessageSearchChange={setMessageSearchTerm}
        onRoomTypeChange={setSelectedRoomTypes}
        onMessageStatusChange={setMessageStatus}
        onSearch={handleSearch}
        onClearFilters={handleClearFilters}
        onClearMessageSearch={() => setMessageSearchTerm('')}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <DeletionHistoryTable
        histories={deletionHistories}
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        loading={loading}
        isDeveloper={isDeveloper()}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Box>
  );
};
export default ChatMessageDeletionHistoryPage;
